/**
 * GPA calculation utilities - Simplified version
 */

/**
 * Convert letter grade to GPA value
 * @param {string|number} grade - Grade (A, B+, 4.0, 93%, etc.)
 * @param {Object} settings - GPA settings with grade mappings
 * @returns {number} GPA value (0-4.0)
 */
export const letterGradeToGPA = (grade, settings) => {
  if (!grade) return 0;

  const gradeStr = String(grade).trim().toUpperCase();

  // Handle GPA values directly
  const numeric = parseFloat(gradeStr);
  if (!isNaN(numeric) && numeric >= 0 && numeric <= 4.0) {
    return numeric;
  }

  // Handle percentages
  if (gradeStr.includes('%')) {
    const percent = parseFloat(gradeStr.replace('%', ''));
    if (!isNaN(percent)) {
      return percent >= 90 ? 4.0 :
             percent >= 80 ? 3.0 :
             percent >= 70 ? 2.0 :
             percent >= 60 ? 1.0 : 0;
    }
  }

  // Handle fractions
  if (gradeStr.includes('/')) {
    const [num, den] = gradeStr.split('/').map(n => parseFloat(n));
    if (!isNaN(num) && !isNaN(den) && den > 0) {
      const percent = (num / den) * 100;
      return percent >= 90 ? 4.0 :
             percent >= 80 ? 3.0 :
             percent >= 70 ? 2.0 :
             percent >= 60 ? 1.0 : 0;
    }
  }

  // Use settings for letter grades
  if (settings?.gpa?.[gradeStr]) {
    return settings.gpa[gradeStr].value || 0;
  }

  // Default letter grade mapping
  const defaultMapping = {
    'A+': 4.0, 'A': 4.0, 'A-': 3.7,
    'B+': 3.3, 'B': 3.0, 'B-': 2.7,
    'C+': 2.3, 'C': 2.0, 'C-': 1.7,
    'D+': 1.3, 'D': 1.0, 'D-': 0.7,
    'F': 0
  };

  return defaultMapping[gradeStr] || 0;
};

/**
 * Convert GPA to letter grade
 * @param {number} gpa - GPA value
 * @param {Object} settings - GPA settings
 * @returns {string} Letter grade
 */
export const gpaToLetterGrade = (gpa, settings) => {
  if (typeof gpa !== 'number' || isNaN(gpa)) return 'F';

  // Use settings if available
  if (settings?.gpa) {
    const sortedGrades = Object.entries(settings.gpa)
      .sort(([,a], [,b]) => b.value - a.value);

    for (const [grade, config] of sortedGrades) {
      if (gpa >= config.value) return grade;
    }
  }

  // Default mapping
  if (gpa >= 3.7) return 'A-';
  if (gpa >= 3.3) return 'B+';
  if (gpa >= 3.0) return 'B';
  if (gpa >= 2.7) return 'B-';
  if (gpa >= 2.3) return 'C+';
  if (gpa >= 2.0) return 'C';
  if (gpa >= 1.7) return 'C-';
  if (gpa >= 1.3) return 'D+';
  if (gpa >= 1.0) return 'D';
  if (gpa >= 0.7) return 'D-';
  return 'F';
};

/**
 * Calculate GPA for a course based on its assignments
 * @param {Object} course - Course object
 * @param {Array} assignments - Assignments for this course
 * @param {Object} settings - GPA settings
 * @returns {number|null} Course GPA or null if no graded assignments
 */
export const calculateCourseGPAProjection = (course, assignments, settings) => {
  if (!assignments || (assignments || []).length === 0) return null;

  const gradedAssignments = (assignments || []).filter(a => a.grade && String(a.grade).trim());

  if (gradedAssignments.length === 0) return null;

  // Calculate weighted average
  let totalWeightedGPA = 0;
  let totalWeight = 0;

  gradedAssignments.forEach(assignment => {
    const gpa = letterGradeToGPA(assignment.grade, settings);
    const weight = parseFloat(assignment.weight) || 1;

    totalWeightedGPA += gpa * weight;
    totalWeight += weight;
  });

  return totalWeight > 0 ? Math.round((totalWeightedGPA / totalWeight) * 100) / 100 : null;
};

/**
 * Calculate overall current GPA
 * @param {Array} courses - All courses
 * @param {Array} assignments - All assignments
 * @param {Object} settings - GPA settings
 * @returns {number} Overall GPA
 */
export const calculateOverallCurrentGPA = (courses, assignments, settings) => {
  if (!courses || (courses || []).length === 0) return 0;

  let totalWeightedGPA = 0;
  let totalCredits = 0;

  (courses || []).forEach(course => {
    const credits = parseFloat(course.credits) || 3;
    let courseGPA = null;

    let courseAssignments = [];
    if (assignments) {
      courseAssignments = (assignments || []).filter(a => {
        const assignmentCourseId = typeof a.course === 'object' ? a.course._id : a.course;
        const courseId = course._id || course.id;
        return String(assignmentCourseId) === String(courseId);
      });
    }

    // Use final course grade when available, otherwise derive from completed assignments.
    if (course.grade && String(course.grade).trim()) {
      courseGPA = letterGradeToGPA(course.grade, settings);
    } else if (courseAssignments.length > 0) {
      courseGPA = calculateCourseGPAProjection(course, courseAssignments, settings);
    }

    if (courseGPA !== null && courseGPA !== undefined) {
      totalWeightedGPA += courseGPA * credits;
      totalCredits += credits;
    }
  });

  return totalCredits > 0 ? Math.round((totalWeightedGPA / totalCredits) * 100) / 100 : 0;
};

/**
 * Get target GPA options
 * @returns {Array} Array of target GPA values
 */
export const getTargetGPAOptions = () => {
  return [2.0, 2.5, 3.0, 3.3, 3.5, 3.7, 4.0];
};

/**
 * Calculate required grades for remaining courses to reach target GPA
 * @param {Array} courses - All courses
 * @param {Array} assignments - All assignments
 * @param {Array} selectedCourseIds - IDs of courses to calculate for
 * @param {number} currentGPA - Current GPA
 * @param {number} targetGPA - Target GPA
 * @param {Object} settings - GPA settings
 * @returns {Object} Required grades information
 */
export const calculateRequiredGradesWithAssignments = (courses, assignments, selectedCourseIds, currentGPA, targetGPA, settings) => {
  if (!courses || !selectedCourseIds || selectedCourseIds.length === 0) {
    return { requiredGPA: targetGPA, courseRequirements: [] };
  }

  // Calculate completed credits and GPA
  const completedCourses = (courses || []).filter(c => c.grade && c.status === 'completed');
  const completedCredits = completedCourses.reduce((sum, c) => sum + (parseFloat(c.credits) || 3), 0);
  const completedWeightedGPA = completedCourses.reduce((sum, c) => {
    const gpa = letterGradeToGPA(c.grade, settings);
    const credits = parseFloat(c.credits) || 3;
    return sum + (gpa * credits);
  }, 0);

  // Calculate remaining credits
  const remainingCourses = (courses || []).filter(c => selectedCourseIds.includes(c._id || c.id));
  const remainingCredits = remainingCourses.reduce((sum, c) => sum + (parseFloat(c.credits) || 3), 0);

  if (remainingCredits === 0) {
    return { requiredGPA: targetGPA, courseRequirements: [] };
  }

  // Calculate required GPA for remaining courses
  const totalTargetCredits = completedCredits + remainingCredits;
  const requiredGPA = ((targetGPA * totalTargetCredits) - completedWeightedGPA) / remainingCredits;

  // Generate course requirements
  const courseRequirements = remainingCourses.map(course => {
    const courseGPA = Math.max(0, Math.min(4.0, requiredGPA));
    return {
      courseId: course._id || course.id,
      courseName: course.name,
      requiredGrade: gpaToLetterGrade(courseGPA, settings),
      requiredGPA: Math.round(courseGPA * 100) / 100
    };
  });

  return {
    requiredGPA: Math.round(requiredGPA * 100) / 100,
    courseRequirements
  };
};

/**
 * Calculate course projection with assignments
 * @param {Object} course - Course object
 * @param {Array} assignments - Course assignments
 * @param {number} targetGPA - Target GPA for course
 * @param {Object} settings - GPA settings
 * @returns {Object} Projection details
 */
export const calculateCourseProjectionWithAssignments = (course, assignments, targetGPA, settings) => {
  if (!assignments || (assignments || []).length === 0) {
    return { requiredGrade: gpaToLetterGrade(targetGPA, settings), requiredGPA: targetGPA };
  }

  const gradedAssignments = (assignments || []).filter(a => a.grade && String(a.grade).trim());
  const ungradedAssignments = (assignments || []).filter(a => !a.grade || !String(a.grade).trim());

  if (ungradedAssignments.length === 0) {
    const currentGPA = calculateCourseGPAProjection(course, gradedAssignments, settings);
    return {
      currentGPA: currentGPA || 0,
      requiredGrade: currentGPA >= targetGPA ? 'Already achieved' : gpaToLetterGrade(targetGPA, settings),
      requiredGPA: targetGPA
    };
  }

  // Calculate current weighted GPA from graded assignments
  let currentWeightedGPA = 0;
  let currentWeight = 0;

  gradedAssignments.forEach(a => {
    const gpa = letterGradeToGPA(a.grade, settings);
    const weight = parseFloat(a.weight) || 1;
    currentWeightedGPA += gpa * weight;
    currentWeight += weight;
  });

  // Calculate remaining weight
  const remainingWeight = ungradedAssignments.reduce((sum, a) => sum + (parseFloat(a.weight) || 1), 0);
  const totalWeight = currentWeight + remainingWeight;

  if (totalWeight === 0) {
    return { requiredGrade: gpaToLetterGrade(targetGPA, settings), requiredGPA: targetGPA };
  }

  // Calculate required GPA for remaining assignments
  const requiredGPA = ((targetGPA * totalWeight) - currentWeightedGPA) / remainingWeight;
  const clampedGPA = Math.max(0, Math.min(4.0, requiredGPA));

  return {
    currentGPA: currentWeight > 0 ? Math.round((currentWeightedGPA / currentWeight) * 100) / 100 : 0,
    requiredGrade: gpaToLetterGrade(clampedGPA, settings),
    requiredGPA: Math.round(clampedGPA * 100) / 100
  };
};

/**
 * Calculate GPA impact of hypothetical assignment grades
 * @param {Array} courses - All courses
 * @param {Array} assignments - All assignments
 * @param {Object} hypotheticalGrades - Assignment ID -> grade mapping
 * @param {Array} selectedAssignments - IDs of assignments to consider
 * @param {Object} settings - GPA settings
 * @returns {Object} GPA impact information
 */
export const calculateAssignmentGPAImpact = (courses, assignments, hypotheticalGrades, selectedAssignments, settings) => {
  if (!assignments || !hypotheticalGrades) {
    return { currentGPA: 0, projectedGPA: 0, difference: 0 };
  }

  const currentGPA = calculateOverallCurrentGPA(courses, assignments, settings);

  // Create modified assignments with hypothetical grades
  const modifiedAssignments = (assignments || []).map(a => {
    if (selectedAssignments.includes(a._id) && hypotheticalGrades[a._id]) {
      return { ...a, grade: hypotheticalGrades[a._id] };
    }
    return a;
  });

  const projectedGPA = calculateOverallCurrentGPA(courses, modifiedAssignments, settings);

  return {
    currentGPA: Math.round(currentGPA * 100) / 100,
    projectedGPA: Math.round(projectedGPA * 100) / 100,
    difference: Math.round((projectedGPA - currentGPA) * 100) / 100
  };
};


/**
 * Parse grade string to percentage (0-100)
 * @param {string} gradeStr - Grade string (e.g., "85", "450/500", "A")
 * @param {Object} [settings] - Optional GPA settings for letter grade conversion
 * @returns {number|null} Percentage value or null if invalid
 */
export const parseGrade = (gradeStr, settings) => {
  if (!gradeStr || typeof gradeStr !== 'string') return null;

  const grade = gradeStr.trim();

  // Handle plain percentage (e.g., "85%")
  if (grade.includes('%')) {
    const num = parseFloat(grade.replace('%', ''));
    if (!isNaN(num) && num >= 0 && num <= 100) {
      return num;
    }
    return null;
  }

  // Handle fraction (e.g., "450/500", "25/25")
  if (grade.includes('/')) {
    const parts = grade.split('/');
    if (parts.length === 2) {
      const studentPoints = parseFloat(parts[0]);
      const totalPoints = parseFloat(parts[1]);
      if (!isNaN(studentPoints) && !isNaN(totalPoints) &&
          studentPoints >= 0 && totalPoints > 0 && totalPoints >= studentPoints) {
        return (studentPoints / totalPoints) * 100;
      }
    }
    return null;
  }

  // Handle plain number as percentage
  // Check if it's a number first to avoid matching letter grades that might start with a number (unlikely but safe)
  // Also ensure it's not a letter grade like "A" which parseFloat returns NaN for
  const num = parseFloat(grade);
  // Check if the string is actually a number (not "12A")
  const isNumeric = !isNaN(num) && isFinite(grade);
  
  if (isNumeric && num >= 0 && num <= 100) {
    return num;
  }

  // Handle Letter Grades
  const upperGrade = grade.toUpperCase();
  
  // Check settings
  if (settings?.gpa && settings.gpa[upperGrade]) {
    // Return the average of min and max for the letter grade
    const { min, max } = settings.gpa[upperGrade];
    return (min + max) / 2;
  }

  // Default Letter Mapping
  const defaultMapping = {
    'A+': 100, 'A': 95, 'A-': 91,
    'B+': 88, 'B': 85, 'B-': 81,
    'C+': 78, 'C': 75, 'C-': 71,
    'D+': 68, 'D': 65, 'D-': 61,
    'F': 0
  };
  
  if (defaultMapping[upperGrade] !== undefined) {
    return defaultMapping[upperGrade];
  }

  return null; // Invalid grade format
};

/**
 * Format percentage to letter grade
 * @param {number} percentage - Percentage value (0-100)
 * @param {Object} settings - GPA settings with grade ranges
 * @returns {string} Letter grade
 */
export const formatGrade = (percentage, settings) => {
  if (typeof percentage !== 'number' || isNaN(percentage)) return 'F';

  // Use settings if available
  if (settings?.gpa) {
    const sortedGrades = Object.entries(settings.gpa)
      .sort(([,a], [,b]) => b.min - a.min);

    for (const [grade, config] of sortedGrades) {
      if (percentage >= config.min) return grade;
    }
  }

  // Default mapping based on percentage
  if (percentage >= 90) return 'A';
  if (percentage >= 80) return 'B';
  if (percentage >= 70) return 'C';
  if (percentage >= 60) return 'D';
  return 'F';
};